import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, EmojiEvents as TrophyIcon, FileDownload as DownloadIcon } from "@mui/icons-material";
import { getJwt } from "../services/authService";

const API_URL = process.env.REACT_APP_API_URL;

const Giveaways = () => {
  const [giveaways, setGiveaways] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [winnersDialogOpen, setWinnersDialogOpen] = useState(false);
  const [currentWinners, setCurrentWinners] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    imageUrl: "",
    endDate: "",
    prize: "",
    minActiveNodes: 3,
    maxWinners: 1,
    isActive: true,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchGiveaways();
  }, []);

  const fetchGiveaways = async () => {
    try {
      const response = await axios.get(`${API_URL}/giveaways/admin`, {
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      setGiveaways(response.data);
    } catch (error) {
      console.error("Error fetching giveaways:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch giveaways",
        severity: "error",
      });
    }
  };

  const handleClickOpen = (giveaway = null) => {
    if (giveaway) {
      setIsEdit(true);
      setSelectedGiveaway(giveaway);
      setFormData({
        title: giveaway.title,
        slug: giveaway.slug,
        description: giveaway.description,
        imageUrl: giveaway.imageUrl || "",
        endDate: new Date(giveaway.endDate).toISOString().split("T")[0],
        prize: giveaway.prize,
        minActiveNodes: giveaway.minActiveNodes,
        maxWinners: giveaway.maxWinners || 1,
        isActive: giveaway.isActive,
      });
    } else {
      setIsEdit(false);
      setSelectedGiveaway(null);
      setFormData({
        title: "",
        slug: "",
        description: "",
        imageUrl: "",
        endDate: "",
        prize: "",
        minActiveNodes: 3,
        maxWinners: 1,
        isActive: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleToggleActive = async (giveaway) => {
    try {
      await axios.put(
        `${API_URL}/giveaways/${giveaway.slug}`,
        { isActive: !giveaway.isActive },
        { headers: { Authorization: `Bearer ${getJwt()}` } }
      );
      fetchGiveaways();
      setSnackbar({
        open: true,
        message: `Giveaway ${giveaway.isActive ? "deactivated" : "activated"} successfully`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error toggling giveaway status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update giveaway status",
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        await axios.put(
          `${API_URL}/giveaways/${selectedGiveaway.slug}`,
          formData,
          { headers: { Authorization: `Bearer ${getJwt()}` } }
        );
        setSnackbar({
          open: true,
          message: "Giveaway updated successfully",
          severity: "success",
        });
      } else {
        await axios.post(
          `${API_URL}/giveaways`,
          formData,
          { headers: { Authorization: `Bearer ${getJwt()}` } }
        );
        setSnackbar({
          open: true,
          message: "Giveaway created successfully",
          severity: "success",
        });
      }
      handleClose();
      fetchGiveaways();
    } catch (error) {
      console.error("Error submitting giveaway:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to save giveaway",
        severity: "error",
      });
    }
  };

  const handleDelete = async (giveaway) => {
    if (window.confirm(`Are you sure you want to delete "${giveaway.title}"?`)) {
      try {
        await axios.delete(`${API_URL}/giveaways/${giveaway.slug}`, {
          headers: { Authorization: `Bearer ${getJwt()}` },
        });
        fetchGiveaways();
        setSnackbar({
          open: true,
          message: "Giveaway deleted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error deleting giveaway:", error);
        setSnackbar({
          open: true,
          message: "Failed to delete giveaway",
          severity: "error",
        });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleSelectWinners = async (giveaway) => {
    const numWinners = prompt(`Enter number of winners (max ${giveaway.maxWinners || 1}):`, "1");
    
    if (!numWinners) return;
    
    const winnersCount = parseInt(numWinners);
    
    if (isNaN(winnersCount) || winnersCount < 1) {
      setSnackbar({
        open: true,
        message: "Please enter a valid number of winners",
        severity: "error",
      });
      return;
    }
    
    if (winnersCount > (giveaway.maxWinners || 1)) {
      setSnackbar({
        open: true,
        message: `Cannot select more than ${giveaway.maxWinners || 1} winners`,
        severity: "error",
      });
      return;
    }
    
    if (window.confirm(`Are you sure you want to select ${winnersCount} winner(s) for "${giveaway.title}"?`)) {
      try {
        const response = await axios.post(
          `${API_URL}/giveaways/${giveaway.slug}/select-winners`,
          { count: winnersCount },
          { headers: { Authorization: `Bearer ${getJwt()}` } }
        );
        fetchGiveaways();
        setSnackbar({
          open: true,
          message: `${winnersCount} winner(s) selected successfully`,
          severity: "success",
        });
      } catch (error) {
        console.error("Error selecting winners:", error);
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to select winners",
          severity: "error",
        });
      }
    }
  };

  const handleViewWinners = async (giveaway) => {
    if (!giveaway.winners || giveaway.winners.length === 0) {
      setSnackbar({
        open: true,
        message: "No winners selected yet",
        severity: "info",
      });
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/giveaways/${giveaway.slug}/winners`, {
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      setCurrentWinners(response.data.winners);
      setWinnersDialogOpen(true);
    } catch (error) {
      console.error("Error fetching winners:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch winners",
        severity: "error",
      });
    }
  };

  const handleCloseWinnersDialog = () => {
    setWinnersDialogOpen(false);
  };

  // Функция для экспорта победителей в CSV
  const exportWinnersToCSV = (masked = false) => {
    if (!currentWinners || currentWinners.length === 0) {
      setSnackbar({
        open: true,
        message: "Нет победителей для экспорта",
        severity: "info",
      });
      return;
    }

    // Создаем CSV контент
    let csvContent = "user\n"; // Заголовок
    
    currentWinners.forEach(winner => {
      let userData = "";
      
      if (winner.email) {
        userData = masked ? maskEmail(winner.email) : winner.email;
      } else if (winner.tg) {
        userData = masked ? maskTelegram(winner.tg) : winner.tg;
      }
      
      if (userData) {
        csvContent += userData + "\n";
      }
    });
    
    // Создаем blob и ссылку для скачивания
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `winners${masked ? '_masked' : ''}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Функция для маскирования email
  const maskEmail = (email) => {
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    
    const name = parts[0];
    const domain = parts[1];
    
    let maskedName = '';
    if (name.length <= 3) {
      maskedName = name[0] + '*'.repeat(name.length - 1);
    } else if (name.length <= 6) {
      // Показываем первые 2 и последние 2 символа
      maskedName = name.substring(0, 3) + '*'.repeat(Math.max(name.length - 5, 1)) + name.substring(name.length - 2);
    } else {
      // Показываем первые 4 и последние 3 символа
      maskedName = name.substring(0, 4) + '*'.repeat(Math.max(name.length - 7, 1)) + name.substring(name.length - 3);
    }
    
    // Разделяем домен на части
    const domainParts = domain.split('.');
    if (domainParts.length > 1) {
      // Показываем полностью доменную часть (после @)
      return `${maskedName}@${domain}`;
    } else {
      return `${maskedName}@${domain}`;
    }
  };
  
  // Функция для маскирования телеграм-аккаунта
  const maskTelegram = (tg) => {
    if (tg.length <= 3) return tg[0] + '*'.repeat(tg.length - 1);
    if (tg.length <= 6) {
      // Показываем первые 3 и последние 2 символа
      return tg.substring(0, 3) + '*'.repeat(Math.max(tg.length - 5, 1)) + tg.substring(tg.length - 2);
    } else {
      // Показываем первые 4 и последние 3 символа
      return tg.substring(0, 4) + '*'.repeat(Math.max(tg.length - 7, 1)) + tg.substring(tg.length - 3);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Giveaways</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleClickOpen()}
        >
          Add Giveaway
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Prize</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Min Active Nodes</TableCell>
              <TableCell>Max Winners</TableCell>
              <TableCell>Participants</TableCell>
              <TableCell>Winners</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {giveaways.map((giveaway) => (
              <TableRow key={giveaway._id}>
                <TableCell>{giveaway.title}</TableCell>
                <TableCell>{giveaway.slug}</TableCell>
                <TableCell>{giveaway.prize}</TableCell>
                <TableCell>{formatDate(giveaway.endDate)}</TableCell>
                <TableCell>{giveaway.minActiveNodes}</TableCell>
                <TableCell>{giveaway.maxWinners || 1}</TableCell>
                <TableCell>{giveaway.participants?.length || 0}</TableCell>
                <TableCell>{giveaway.winners?.length || 0}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color={giveaway.isActive ? "success" : "error"}
                    size="small"
                    onClick={() => handleToggleActive(giveaway)}
                  >
                    {giveaway.isActive ? "Active" : "Inactive"}
                  </Button>
                </TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleClickOpen(giveaway)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(giveaway)}>
                    <DeleteIcon />
                  </IconButton>
                  {(giveaway.winners?.length || 0) < (giveaway.maxWinners || 1) && giveaway.participants?.length > 0 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => handleSelectWinners(giveaway)}
                    >
                      Select Winners
                    </Button>
                  )}
                  {(giveaway.winners?.length || 0) > 0 && (
                    <IconButton 
                      color="primary" 
                      onClick={() => handleViewWinners(giveaway)}
                      title="View Winners"
                    >
                      <TrophyIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEdit ? "Edit Giveaway" : "Add Giveaway"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                autoFocus
                margin="dense"
                name="title"
                label="Title"
                type="text"
                fullWidth
                value={formData.title}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                name="slug"
                label="Slug (URL-friendly name, e.g., 'dria')"
                type="text"
                fullWidth
                value={formData.slug}
                onChange={handleChange}
                helperText="Leave blank to auto-generate from title"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="description"
                label="Description"
                multiline
                rows={4}
                fullWidth
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                name="imageUrl"
                label="Image URL"
                type="text"
                fullWidth
                value={formData.imageUrl}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                name="prize"
                label="Prize"
                type="text"
                fullWidth
                value={formData.prize}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                name="endDate"
                label="End Date"
                type="date"
                fullWidth
                value={formData.endDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                name="minActiveNodes"
                label="Minimum Active Nodes Required"
                type="number"
                fullWidth
                value={formData.minActiveNodes}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                name="maxWinners"
                label="Maximum Number of Winners"
                type="number"
                fullWidth
                value={formData.maxWinners}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEdit ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Winners Dialog */}
      <Dialog open={winnersDialogOpen} onClose={handleCloseWinnersDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Winners</DialogTitle>
        <DialogContent>
          <List>
            {currentWinners.map((winner, index) => (
              <React.Fragment key={winner.id}>
                <ListItem>
                  <ListItemText
                    primary={winner.email || winner.tg}
                    secondary={`Selected: ${new Date(winner.selectedAt).toLocaleString()}`}
                  />
                </ListItem>
                {index < currentWinners.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={1}>
            <Button 
              startIcon={<DownloadIcon />} 
              variant="outlined" 
              color="primary" 
              onClick={() => exportWinnersToCSV(false)}
            >
              Выгрузить победителей
            </Button>
            <Button 
              startIcon={<DownloadIcon />} 
              variant="outlined" 
              color="secondary" 
              onClick={() => exportWinnersToCSV(true)}
            >
              Выгрузить победителей (маскированные)
            </Button>
            <Button onClick={handleCloseWinnersDialog}>Close</Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Giveaways; 